import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../services/auth.service';
import type { MitLimits, MitMandateDTO, MitTransactionDTO, MitTransactionStatusValue, MitUsageDTO } from '../../core/models';
import {
  MIT_CONSENT_TYPE_LABELS,
  MIT_LIMIT_ROWS,
  MIT_MANDATE_STATUS_LABELS,
  MIT_TRANSACTION_STATUS_LABELS,
  MIT_TRANSACTION_TYPE_LABELS,
  MitLimitRow,
  MitService,
  mitLimitValue,
  mitMandateStatusReason,
  mitMandateStatusSeverity,
  mitRejectionLabel,
  mitTransactionStatusSeverity
} from '../../services/mit.service';

interface UsageBar {
  label: string;
  used: number;
  limit: number | null | undefined;
  kind: 'amount' | 'count';
}

@Component({
  selector: 'app-mit-mandate-summary',
  templateUrl: './mit-mandate-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterLink, TagModule, ProgressBarModule, TooltipModule]
})
export class MitMandateSummaryComponent {
  mandate = input.required<MitMandateDTO>();

  private mitService = inject(MitService);
  private auth = inject(AuthService);

  readonly limitRows = MIT_LIMIT_ROWS;
  readonly canReadTransactions = this.auth.hasRole('MIT_TRANSACTION_READ');
  readonly canSearchTransactions = this.auth.hasRole('MIT_TRANSACTION_SEARCH');
  private readonly transactionsSize = 5;

  usage = signal<MitUsageDTO | null>(null);
  transactions = signal<MitTransactionDTO[]>([]);
  transactionsTotal = signal(0);
  transactionsLoading = signal(false);

  statusSeverity = mitMandateStatusSeverity;
  txSeverity = mitTransactionStatusSeverity;

  statusLabel = computed(() => {
    const status = this.mandate().status;
    return status ? MIT_MANDATE_STATUS_LABELS[status] : '—';
  });
  statusReason = computed(() => mitMandateStatusReason(this.mandate()));
  cardExpiresSoon = computed(() => {
    const card = this.mandate().card;
    if (!card?.expirationMonth || !card.expirationYear) return false;
    const year = Number(card.expirationYear.length === 2 ? `20${card.expirationYear}` : card.expirationYear);
    const lastDay = new Date(year, Number(card.expirationMonth), 0, 23, 59, 59);
    return (lastDay.getTime() - Date.now()) / 86_400_000 <= 30;
  });
  usageBars = computed<UsageBar[]>(() => {
    const usage = this.usage();
    if (!usage) return [];
    const limits = usage.effectiveLimits ?? this.mandate().limits;
    return [
      { label: 'Monto hoy', used: usage.dayAmount ?? 0, limit: limits?.maxAmountPerDay, kind: 'amount' },
      { label: 'Monto este mes', used: usage.monthAmount ?? 0, limit: limits?.maxAmountPerMonth, kind: 'amount' },
      { label: 'Monto acumulado', used: usage.totalAmount ?? 0, limit: limits?.maxTotalAmount, kind: 'amount' },
      { label: 'Cobros hoy', used: usage.dayCount ?? 0, limit: limits?.maxTransactionsPerDay, kind: 'count' },
      { label: 'Cobros este mes', used: usage.monthCount ?? 0, limit: limits?.maxTransactionsPerMonth, kind: 'count' },
      { label: 'Cobros en total', used: usage.totalCount ?? 0, limit: limits?.maxTotalTransactions, kind: 'count' }
    ];
  });

  private mandateId = computed(() => this.mandate().id);

  constructor() {
    effect(() => {
      const id = this.mandateId();
      untracked(() => this.loadRelated(id));
    });
  }

  limitValue(row: MitLimitRow, limits: MitLimits | null | undefined): string {
    return mitLimitValue(row, limits);
  }

  limitChanged(row: MitLimitRow): boolean {
    const m = this.mandate();
    return !!m.pendingLimits && m.pendingLimits[row.key] !== m.limits?.[row.key];
  }

  barPercent(bar: UsageBar): number {
    if (!bar.limit) return 0;
    return Math.min(100, Math.round((bar.used / bar.limit) * 100));
  }

  consentLabel(type: string | undefined): string {
    return type ? MIT_CONSENT_TYPE_LABELS[type as keyof typeof MIT_CONSENT_TYPE_LABELS] ?? type : '—';
  }

  txStatusLabel(status: MitTransactionStatusValue | undefined): string {
    return status ? MIT_TRANSACTION_STATUS_LABELS[status] : '—';
  }

  txTypeLabel(type: MitTransactionDTO['type']): string {
    return type ? MIT_TRANSACTION_TYPE_LABELS[type] : '—';
  }

  txDetail(tx: MitTransactionDTO): string {
    if (tx.status === 'REJECTED') return mitRejectionLabel(tx.rejectionCode);
    if (tx.status === 'DECLINED') return tx.providerMessage ?? '';
    return '';
  }

  private loadRelated(id: number | undefined): void {
    this.usage.set(null);
    this.transactions.set([]);
    this.transactionsTotal.set(0);
    if (!id) return;
    this.mitService.usage(id).subscribe({
      next: (response) => this.usage.set(response.body ?? null),
      error: () => this.usage.set(null)
    });
    if (!this.canSearchTransactions) return;
    this.transactionsLoading.set(true);
    this.mitService.mandateTransactions(id, {}, { page: 0, size: this.transactionsSize, sort: ['createdAt,desc'] }).subscribe({
      next: (response) => {
        this.transactions.set(response.body ?? []);
        this.transactionsTotal.set(response.page?.totalItems ?? response.body?.length ?? 0);
        this.transactionsLoading.set(false);
      },
      error: () => this.transactionsLoading.set(false)
    });
  }
}
